import { IAnnouncement } from 'announcements/types';
import { SiteRole } from 'types';

const DISMISSED_PREFIX = 'kf-announcements.dismissed';

const parseStringArray = (value: string | null): string[] => {
  if (!value) return [];

  try {
    const parsed: unknown = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item): item is string => typeof item === 'string');
  } catch {
    return [];
  }
};

const AnnouncementService = {
  /** Dismissal is scoped by user so a user only sees an announcement once across all roles/sites. */
  buildAnnouncementDismissedKey: (userId: string): string => `${DISMISSED_PREFIX}::user::${userId}`,

  /** Get the IDs of announcements that have been dismissed by a user. */
  getDismissedIds: (userId: string | undefined): string[] => (userId ? parseStringArray(localStorage.getItem(AnnouncementService.buildAnnouncementDismissedKey(userId))) : []),

  /** Check if an announcement has been dismissed by a user. */
  isDismissed: (userId: string | undefined, announcementId: string): boolean => (userId ? AnnouncementService.getDismissedIds(userId).includes(announcementId) : false),

  /** Check if an announcement has expired. */
  isAnnouncementExpired: (announcement: IAnnouncement): boolean => {
    const { expiresAt } = announcement;
    if (!expiresAt) return false;

    const ms = new Date(expiresAt).getTime();
    if (Number.isNaN(ms)) return false;

    return ms <= Date.now();
  },

  /** Check if an announcement has started. Announcements without a startsAt (or an unparseable one) are treated as already started for backward compatibility. */
  isAnnouncementStarted: (announcement: IAnnouncement): boolean => {
    const { startsAt } = announcement;
    if (!startsAt) return true;

    const ms = new Date(startsAt).getTime();
    if (Number.isNaN(ms)) return true;

    return ms <= Date.now();
  },

  /** Get the eligible announcements for a user. */
  getEligibleAnnouncements: (announcements: IAnnouncement[], role: SiteRole, userId: string | undefined): IAnnouncement[] => {
    const dismissedIds = new Set(AnnouncementService.getDismissedIds(userId));

    return announcements.filter(
      (announcement) => announcement.roles.includes(role) && AnnouncementService.isAnnouncementStarted(announcement) && !AnnouncementService.isAnnouncementExpired(announcement) && !dismissedIds.has(announcement.id)
    );
  },

  /** First eligible announcement in source array order. */
  getActiveAnnouncement: (announcements?: IAnnouncement[], role?: SiteRole, userId?: string): IAnnouncement | undefined => {
    if (!role || !announcements) return undefined;

    const eligible = AnnouncementService.getEligibleAnnouncements(announcements, role, userId);
    return eligible[0];
  },

  /** Dismiss one or more announcements for a user. */
  dismiss: (userId: string | undefined, announcementIds: string[]): void => {
    if (!userId || announcementIds.length === 0) return;

    const key = AnnouncementService.buildAnnouncementDismissedKey(userId);
    const dismissedIds = AnnouncementService.getDismissedIds(userId);
    const newDismissedIds = announcementIds.filter((id) => !dismissedIds.includes(id));
    if (newDismissedIds.length === 0) return;

    localStorage.setItem(key, JSON.stringify([...dismissedIds, ...newDismissedIds]));
  },
};

export default AnnouncementService;
